/**
 * AllEventsAutocomplete - Standalone event search for autocomplete
 *
 * Searches across published_events, exhibitions_search, and eventseries_search
 * indexes using direct Algolia queries without the default site search filters.
 *
 * Uses Fuse.js for local fuzzy matching to sort results by relevance,
 * ensuring exact matches appear first.
 */

import Fuse from 'fuse.js'
import { searchAlgoliaIndexes } from '../../modules/algoliaDirectSearch'
import { Locale } from '../../modules/locale'

// Indexes to search for all event types (events, exhibitions, event series)
const ALL_EVENTS_INDEXES = ['published_events', 'exhibitions_search', 'eventseries_search']

export class AllEventsAutocomplete {
  private static _instance: AllEventsAutocomplete
  public static get instance(): AllEventsAutocomplete {
    return this._instance || (this._instance = new this())
  }

  /**
   * Search for events across all event type indexes.
   * Returns combined results from published_events, exhibitions_search, and eventseries_search.
   * Results are sorted by relevance using Fuse.js fuzzy matching.
   *
   * @param text - Search query text
   * @returns Array of event items from all searched indexes, sorted by relevance
   */
  public async getSuggestions(text: string): Promise<any[]> {
    const results = await searchAlgoliaIndexes({
      indexes: ALL_EVENTS_INDEXES,
      query: text,
      hitsPerPage: 30,
    })

    // Combine results from all three event type indexes
    const events = results?.published_events?.items || []
    const exhibitions = results?.exhibitions_search?.items || []
    const eventSeries = results?.eventseries_search?.items || []

    const allItems = [...events, ...exhibitions, ...eventSeries]

    // If no search text, return items as-is (initial load)
    if (!text || text.trim().length === 0) {
      return allItems
    }

    // Use Fuse.js to sort results by relevance to the search query
    // This ensures exact matches appear first
    return this.sortByRelevance(allItems, text)
  }

  /**
   * Sorts items by relevance to the search query using Fuse.js fuzzy matching.
   * Exact matches and close matches will appear first.
   */
  private sortByRelevance(items: any[], query: string): any[] {
    const nameKey = Locale.getInstance().getLanguage() === 'en' ? 'nameEn' : 'nameFr'

    const fuse = new Fuse(items, {
      keys: [nameKey, 'nameFr', 'nameEn'], // Search in both language fields
      threshold: 0.4, // Allow some fuzziness
      includeScore: true,
    })

    const searchResults = fuse.search(query)

    // Return the matched items sorted by score (best matches first)
    return searchResults.map(result => result.item)
  }
}
